import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MdOutlinePersonAddAlt, MdOutlineSmartToy } from "react-icons/md";
import { _adminVendorOnboarding, _createVendorRegistration, _vendorCategories } from "Services/api";
import InputField from "components/fields/InputField";
import SelectField from "components/fields/SelectField";
import TextField from "components/fields/TextField";
import SectionCard from "components/card/SectionCard";

const countryCodeOptions = [
  { value: "+234", label: "+234 (Nigeria)" },
  { value: "+91", label: "+91 (India)" },
  { value: "+1", label: "+1 (USA)" },
  { value: "+44", label: "+44 (UK)" },
  { value: "+81", label: "+81 (Japan)" },
  { value: "+61", label: "+61 (Australia)" },
  { value: "+49", label: "+49 (Germany)" },
  { value: "+86", label: "+86 (China)" },
  { value: "+33", label: "+33 (France)" },
  { value: "+55", label: "+55 (Brazil)" },
];

const initialVendor = {
  first_name: "",
  last_name: "",
  email: "",
  country_code: "+234",
  phone_number: "",
  password: "",
  username: "",
};

const initialOrganization = {
  name: "",
  website: "",
  description: "",
};

const initialBot = {
  botname: "",
  welcome_message: "Hi! How can we help?",
  bot_color_code: "#007bff",
  bot_category_id: "",
};

const ActionButton = ({ loading, children }) => (
  <button
    type="submit"
    disabled={loading}
    className="flex w-full items-center justify-center rounded-xl bg-brand-500 px-4 py-3 font-medium text-white shadow-sm transition hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
  >
    {loading ? "Creating..." : children}
  </button>
);

const SubSectionHeading = ({ step, title }) => (
  <div className="md:col-span-2">
    <div className="mb-1 mt-4 flex items-center gap-2 border-b border-gray-100 pb-2 dark:border-white/10">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">
        {step}
      </span>
      <h3 className="text-base font-bold text-navy-700 dark:text-white">{title}</h3>
    </div>
  </div>
);

const FieldGrid = ({
  vendor,
  organization,
  bot,
  onVendorChange,
  onOrganizationChange,
  onBotChange,
  includeBot,
  categoryOptions,
  loadingCategories,
}) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
    <SubSectionHeading step={1} title="Vendor Details" />
    <InputField
      label="First Name"
      value={vendor.first_name}
      onChange={(event) => onVendorChange("first_name", event.target.value)}
      required
    />
    <InputField
      label="Last Name"
      value={vendor.last_name}
      onChange={(event) => onVendorChange("last_name", event.target.value)}
      required
    />
    <InputField
      label="Email"
      type="email"
      value={vendor.email}
      onChange={(event) => onVendorChange("email", event.target.value)}
      required
    />
    <InputField
      label="Username"
      value={vendor.username}
      onChange={(event) => onVendorChange("username", event.target.value)}
    />
    <SelectField
      label="Country Code"
      value={vendor.country_code}
      options={countryCodeOptions}
      onChange={(event) => onVendorChange("country_code", event.target.value)}
      className="w-full"
      required
    />
    <InputField
      label="Phone Number"
      value={vendor.phone_number}
      onChange={(event) => onVendorChange("phone_number", event.target.value)}
      required
    />
    <div className="md:col-span-2">
      <InputField
        label="Password"
        type="password"
        value={vendor.password}
        onChange={(event) => onVendorChange("password", event.target.value)}
        required
      />
    </div>

    <SubSectionHeading step={2} title="Organization" />
    <InputField
      label="Organization Name"
      value={organization.name}
      onChange={(event) => onOrganizationChange("name", event.target.value)}
      required
    />
    <InputField
      label="Website"
      value={organization.website}
      onChange={(event) => onOrganizationChange("website", event.target.value)}
      required
    />
    <div className="md:col-span-2">
      <TextField
        label="Description"
        rows={4}
        value={organization.description}
        onChange={(event) => onOrganizationChange("description", event.target.value)}
        required
      />
    </div>

    {includeBot ? (
      <>
        <SubSectionHeading step={3} title="Bot Setup" />
        <InputField
          label="Bot Name"
          value={bot.botname}
          onChange={(event) => onBotChange("botname", event.target.value)}
          required
        />
        <SelectField
          label="Bot Category"
          value={bot.bot_category_id}
          options={categoryOptions}
          onChange={(event) => onBotChange("bot_category_id", event.target.value)}
          disabled={loadingCategories}
          className="w-full"
          required
        />
        <InputField
          label="Bot Color"
          type="color"
          value={bot.bot_color_code}
          onChange={(event) => onBotChange("bot_color_code", event.target.value)}
          className="h-12 cursor-pointer p-1"
        />
        <InputField
          label="Welcome Message"
          value={bot.welcome_message}
          onChange={(event) => onBotChange("welcome_message", event.target.value)}
        />
      </>
    ) : null}
  </div>
);

const VendorOnboarding = () => {
  const accessToken = sessionStorage.getItem("VendorToken");
  const [vendor, setVendor] = useState(initialVendor);
  const [organization, setOrganization] = useState(initialOrganization);
  const [bot, setBot] = useState(initialBot);
  const [isCreatingVendor, setIsCreatingVendor] = useState(false);
  const [isCreatingOnboarding, setIsCreatingOnboarding] = useState(false);
  const [activeTab, setActiveTab] = useState("vendor");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const categoryOptions = useMemo(
    () => [
      {
        value: "",
        label: loadingCategories ? "Loading categories..." : "Select bot category",
      },
      ...categories.map((category) => ({
        value: String(category.category_id || category.id || category.value),
        label: category.category_name || category.name || category.label || "Unnamed category",
      })),
    ],
    [categories, loadingCategories]
  );

  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await _vendorCategories();
        if (response.status) {
          setCategories(response?.data?.categories || []);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Could not load bot categories.");
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const updateVendor = (key, value) => setVendor((current) => ({ ...current, [key]: value }));
  const updateOrganization = (key, value) =>
    setOrganization((current) => ({ ...current, [key]: value }));
  const updateBot = (key, value) => setBot((current) => ({ ...current, [key]: value }));

  const resetForms = () => {
    setVendor(initialVendor);
    setOrganization(initialOrganization);
    setBot(initialBot);
  };

  const buildVendorRegistrationPayload = () => ({
    vendor,
    organization,
  });

  const buildOnboardingPayload = () => ({
    vendor,
    organization,
    bot: {
      ...bot,
      tone: "formal",
      bot_behavior_prompt: "hello",
      currency: "ngn",
      lang: "english",
    },
  });

  const handleCreateVendor = async (event) => {
    event.preventDefault();
    setIsCreatingVendor(true);
    try {
      const response = await _createVendorRegistration({
        body: buildVendorRegistrationPayload(),
        accessToken,
      });
      toast.success(response.message || "Vendor and organization created successfully.");
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not create vendor and organization.");
    } finally {
      setIsCreatingVendor(false);
    }
  };

  const handleCreateOnboarding = async (event) => {
    event.preventDefault();
    setIsCreatingOnboarding(true);
    try {
      const response = await _adminVendorOnboarding({
        body: buildOnboardingPayload(),
        accessToken,
      });
      toast.success(response.message || "Vendor, organization, and bot created successfully.");
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not complete vendor onboarding.");
    } finally {
      setIsCreatingOnboarding(false);
    }
  };

  const tabs = [
    {
      key: "vendor",
      label: "Vendor + Organization",
      icon: <MdOutlinePersonAddAlt />,
    },
    {
      key: "onboarding",
      label: "Vendor + Organization + Bot",
      icon: <MdOutlineSmartToy />,
    },
  ];

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex w-full gap-2 rounded-2xl bg-white p-2 shadow-lg shadow-gray-200/60 dark:bg-navy-800 dark:shadow-none">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition ${
              activeTab === tab.key
                ? "bg-brand-500 text-white shadow-sm"
                : "text-gray-500 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "vendor" ? (
        <SectionCard
          title="Create Vendor And Organization"
          description="Creates an active vendor and organization from the admin account."
          icon={<MdOutlinePersonAddAlt />}
        >
          <form onSubmit={handleCreateVendor} className="space-y-4">
            <FieldGrid
              vendor={vendor}
              organization={organization}
              bot={bot}
              onVendorChange={updateVendor}
              onOrganizationChange={updateOrganization}
              onBotChange={updateBot}
              categoryOptions={categoryOptions}
              loadingCategories={loadingCategories}
            />
            <ActionButton loading={isCreatingVendor}>Create Vendor And Organization</ActionButton>
          </form>
        </SectionCard>
      ) : (
        <SectionCard
          title="Create Vendor, Organization And Bot"
          description="Use this when the bot should be created in the same admin onboarding flow."
          icon={<MdOutlineSmartToy />}
        >
          <form onSubmit={handleCreateOnboarding} className="space-y-4">
            <FieldGrid
              vendor={vendor}
              organization={organization}
              bot={bot}
              onVendorChange={updateVendor}
              onOrganizationChange={updateOrganization}
              onBotChange={updateBot}
              categoryOptions={categoryOptions}
              loadingCategories={loadingCategories}
              includeBot
            />
            <ActionButton loading={isCreatingOnboarding}>
              Create Vendor, Organization And Bot
            </ActionButton>
          </form>
        </SectionCard>
      )}
    </div>
  );
};

export default VendorOnboarding;
